/*
 * Custom Goals — cart drawer progress bar with configurable milestones.
 *
 * Vanilla port of the live Vue theme feature (CartStore.ts + CustomGoalsBar.vue +
 * ModalCart.vue). It renders a progress bar with up to 3 goals grouped by threshold,
 * a "reached / amount left" message pair, an optional installments line under the cart
 * total, and — for `gift` goals — auto-adds/removes a gift product as the subtotal
 * crosses the threshold.
 *
 * The element sits persistently inside `.drawer__body` (sibling of #CartDrawerBody),
 * so it survives the innerHTML re-render of the cart regions and self-updates on the
 * `cart:updated` DOM event dispatched by cart.js after every mutation.
 */
(() => {
  'use strict';

  if (customElements.get('custom-goals-bar')) return;

  // ── Module-level state (survives element re-creation, e.g. on the /cart page) ──
  let isProcessingGift = false; // anti-recursion guard for the gift add/remove cycle
  let initialReconcileDone = false; // run the initial gift reconciliation only once
  let GOALS = []; // enabled goals, ascending by threshold (shared across instances)

  const routes = window.FoxTheme.routes;

  // ── Currency formatting (mirrors the live theme's Intl.NumberFormat helper) ──
  const shopify = window.Shopify || {};
  const rate = Number((shopify.currency && shopify.currency.rate) || 1) || 1;
  const activeCurrency = (shopify.currency && shopify.currency.active) || 'ARS';
  const locale = shopify.locale
    ? (shopify.locale.includes('-') ? shopify.locale : `${shopify.locale}-${shopify.country}`)
    : document.documentElement.lang || 'es-AR';

  let currencyFormatter;
  try {
    currencyFormatter = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: activeCurrency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });
  } catch (e) {
    currencyFormatter = new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });
  }

  // Amounts are in store-currency units (not cents), like the Vue implementation.
  const formatCurrency = (units) => currencyFormatter.format(units).replace(/&nbsp;|\s/g, '');

  // Subtotal used by the bar: sum of final_line_price for non gift-card lines, in units.
  const totalFromCart = (cart) =>
    (cart.items || [])
      .filter((item) => !item.gift_card)
      .reduce((sum, item) => sum + item.final_line_price, 0) / 100;

  const getSectionId = () => {
    const el = document.querySelector('custom-goals-bar');
    return el ? el.dataset.sectionId : null;
  };

  /**
   * Gift reconciliation: keep the auto-gift line in sync with the current subtotal.
   * Runs its own add/remove + refresh cycle and publishes `cartUpdate` synchronously
   * inside the guarded block, so the nested `cart:updated` is skipped (guard is still
   * held) and the `finally` clears the guard only after the whole chain settles.
   */
  async function reconcileGift(cart, total) {
    if (isProcessingGift) return;

    const giftGoals = GOALS.filter((g) => g.type === 'gift' && g.productGift && g.isEnabled);
    const giftItem = (cart.items || []).find((item) => item.properties && item.properties._isGift);
    // Single target: highest reached gift threshold (rate-aware).
    const target =
      giftGoals
        .filter((g) => total >= g.threshold * rate)
        .sort((a, b) => b.threshold - a.threshold)[0] || null;

    let action = null;
    if (giftItem && !target) action = 'remove';
    else if (!giftItem && target) action = 'add';
    else if (giftItem && target && String(giftItem.id) !== String(target.productGift)) action = 'swap';
    if (!action) return;

    const sectionId = getSectionId();
    const sectionsUrl = window.location.pathname;

    isProcessingGift = true;
    try {
      let cartData = null;
      let sections = null;

      if (action === 'remove' || action === 'swap') {
        // change.js returns the full cart (+ sections). Use item.key (line key),
        // never item.id (variant id), so we remove the exact gift line.
        cartData = await fetch(routes.cart_change_url, {
          ...window.FoxTheme.utils.fetchConfig(),
          body: JSON.stringify({
            id: giftItem.key,
            quantity: 0,
            sections: [sectionId],
            sections_url: sectionsUrl,
          }),
        }).then((r) => r.json());
        sections = cartData.sections;
      }

      if (action === 'add' || action === 'swap') {
        const addResponse = await fetch(routes.cart_add_url, {
          ...window.FoxTheme.utils.fetchConfig(),
          body: JSON.stringify({
            items: [
              {
                id: Number(target.productGift),
                quantity: 1,
                properties: {
                  _isGift: true,
                  _customImage: target.productGiftImage,
                  _customTitle: target.productGiftTitle,
                  _goalThreshold: target.threshold,
                },
              },
            ],
            sections: [sectionId],
            sections_url: sectionsUrl,
          }),
        }).then((r) => r.json());

        if (addResponse && addResponse.sections) sections = addResponse.sections;
        // add.js does not return the full cart — read it back for accurate totals.
        cartData = await fetch(`${routes.cart_url}.js`).then((r) => r.json());
      }

      if (!cartData || !sections || !sections[sectionId]) {
        // Abnormal: the Cart AJAX API always returns the requested section HTML.
        // The mutation already happened server-side, so fall back to a full drawer
        // refresh instead of leaving the UI stale.
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        return;
      }
      cartData.sections = sections;
      window.FoxTheme.pubsub.publish(window.FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
        cart: cartData,
      });
    } catch (error) {
      console.error('[custom-goals] gift reconciliation failed', error);
    } finally {
      isProcessingGift = false;
    }
  }

  customElements.define(
    'custom-goals-bar',
    class CustomGoalsBar extends HTMLElement {
      connectedCallback() {
        GOALS = this.parseGoals();
        this.defaultInstallments = this.dataset.defaultInstallments === 'true';
        this.maxThreshold = GOALS.length ? GOALS[GOALS.length - 1].threshold : 0;

        this.fillEl = this.querySelector('.custom-goals__fill');
        this.goalsEl = this.querySelector('.custom-goals__goals');
        this.msg1El = this.querySelector('[data-cg-message1]');
        this.msg2El = this.querySelector('[data-cg-message2]');

        this.buildGroups();

        // Paint from the server-rendered subtotal to avoid a flash of empty state.
        this.paint(Number(this.dataset.totalPrice || 0) / 100);

        this.onCartUpdated = (event) => this.handleUpdate(event.detail.cart);
        document.addEventListener('cart:updated', this.onCartUpdated);

        // Mirror the Vue store: reconcile the gift once per page load. Skip the extra
        // /cart.js request when there is nothing to reconcile (no gift goal and no gift
        // line in the cart) — the reconciliation would be a no-op in that case.
        const hasGiftGoal = GOALS.some((g) => g.type === 'gift' && g.productGift && g.isEnabled);
        const hasGiftInCart = this.dataset.hasGift === 'true';
        if (!initialReconcileDone && (hasGiftGoal || hasGiftInCart)) {
          initialReconcileDone = true;
          fetch(`${routes.cart_url}.js`)
            .then((r) => r.json())
            .then((cart) => {
              const total = totalFromCart(cart);
              this.paint(total);
              reconcileGift(cart, total);
            })
            .catch((error) => console.error('[custom-goals] initial cart fetch failed', error));
        }
      }

      disconnectedCallback() {
        if (this.onCartUpdated) document.removeEventListener('cart:updated', this.onCartUpdated);
      }

      parseGoals() {
        const script = this.querySelector('[data-cg-goals]');
        if (!script) return [];
        let parsed = [];
        try {
          parsed = JSON.parse(script.textContent);
        } catch (error) {
          console.error('[custom-goals] could not parse goals', error);
          return [];
        }
        return parsed
          .filter((goal) => goal.isEnabled)
          .sort((a, b) => Number(a.threshold) - Number(b.threshold));
      }

      // Static icon groups (goals sharing a threshold are grouped and positioned once).
      buildGroups() {
        this.groups = [];
        if (!this.goalsEl || !GOALS.length) return;

        const byThreshold = new Map();
        GOALS.forEach((goal) => {
          const threshold = Number(goal.threshold);
          if (!byThreshold.has(threshold)) byThreshold.set(threshold, []);
          byThreshold.get(threshold).push(goal);
        });

        Array.from(byThreshold.entries())
          .sort((a, b) => a[0] - b[0])
          .forEach(([threshold, goals]) => {
            const groupEl = document.createElement('div');
            groupEl.className = 'custom-goals__group';
            Object.assign(groupEl.style, this.groupPosition(threshold));

            goals.forEach((goal) => {
              const goalEl = document.createElement('div');
              goalEl.className = 'custom-goals__goal';
              if (goal.icon) {
                const img = document.createElement('img');
                img.src = goal.icon;
                img.alt = 'goal';
                goalEl.appendChild(img);
              }
              groupEl.appendChild(goalEl);
            });

            this.goalsEl.appendChild(groupEl);
            this.groups.push({
              threshold,
              el: groupEl,
              goalEls: Array.from(groupEl.querySelectorAll('.custom-goals__goal')),
              reachedHtml: goals.map((g) => g.reachedMessage || '').filter(Boolean).join(' '),
              // Keep messages separate so ### is replaced per message (matches the Vue).
              amountLeftMessages: goals.map((g) => g.amountLeftMessage || '').filter(Boolean),
            });
          });
      }

      groupPosition(threshold) {
        if (!this.maxThreshold) return { left: '0px' };
        const percent = (threshold / this.maxThreshold) * 100;
        if (percent < 10) return { left: '0px', transform: 'none' };
        if (percent === 100) return { left: '100%', transform: 'translateX(-100%)' };
        return { left: `${percent}%`, transform: 'translateX(-50%)' };
      }

      handleUpdate(cart) {
        if (!cart) return;
        const total = totalFromCart(cart);
        this.paint(total);
        reconcileGift(cart, total);
      }

      paint(total) {
        this.updateBar(total);
        this.updateInstallments(total);
      }

      updateBar(total) {
        const max = this.maxThreshold * rate;
        const percent = max > 0 && total > 0 ? Math.min(100, (total * 100) / max) : 0;

        if (this.fillEl) {
          this.fillEl.style.width = `${percent}%`;
          this.fillEl.classList.toggle('has-progress', total > 0);
        }

        let reachedHtml = '';
        let amountLeftHtml = '';
        let amountLeftDone = false;
        this.groups.forEach((group) => {
          const threshold = group.threshold * rate;
          const done = total >= threshold;
          group.el.classList.toggle('is-done', done);
          group.goalEls.forEach((el) => el.classList.toggle('is-done', done));
          if (done) reachedHtml = group.reachedHtml; // last reached (ascending) = highest
          if (!amountLeftDone && threshold > total) {
            // First not-reached group. Replace ### in each message (first occurrence
            // per message, like the Vue's String.replace) then join.
            amountLeftDone = true;
            const money = formatCurrency(threshold - total);
            amountLeftHtml = group.amountLeftMessages.map((message) => message.replace('###', money)).join(' ');
          }
        });

        if (this.msg1El) this.msg1El.innerHTML = reachedHtml;
        if (this.msg2El) this.msg2El.innerHTML = amountLeftHtml;
      }

      updateInstallments(total) {
        const sectionId = this.dataset.sectionId;
        const footer =
          document.getElementById(`CartDrawerFooter-${sectionId}`) ||
          document.getElementById(`MainCart-${sectionId}`);
        if (!footer) return;

        // Remove any line we injected earlier — the footer is not always re-rendered
        // between paints (e.g. the initial double paint), so avoid duplicates/stale.
        const previous = footer.querySelector('.custom-goals__installments');
        if (previous) previous.remove();

        const subtotal = footer.querySelector('.totals__subtotal-value');
        if (!subtotal) return;

        const message = this.installmentsMessage(total);
        if (!message) return;

        const span = document.createElement('span');
        span.className = 'custom-goals__installments';
        span.textContent = message;
        // Place it on its own line below the total row (not inside the flex row),
        // like the live theme's `.cart--footer__installments`.
        const totalsRow = subtotal.closest('.totals');
        (totalsRow || subtotal).insertAdjacentElement('afterend', span);
      }

      installmentsMessage(total) {
        if (!total) return '';

        const reached = GOALS.filter(
          (goal) => goal.type === 'cuotas' && goal.isEnabled && total >= goal.threshold * rate
        ).sort((a, b) => b.threshold - a.threshold)[0];

        if (reached) {
          return `o hasta ${reached.installments} cuotas de ${formatCurrency(total / reached.installments)}`;
        }
        if (this.defaultInstallments) {
          return `o hasta 3 cuotas de ${formatCurrency(total / 3)}`;
        }
        return '';
      }
    }
  );
})();
